import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import CyberneticLoader from '@/components/CyberneticLoader';
import { MapProvider } from '@/components/MapProvider';
import { 
  Map, 
  AdvancedMarker,
  useMap
} from '@vis.gl/react-google-maps';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Activity,
  Users,
  Package,
  Clock,
  TrendingUp,
  MapPin,
  Bike,
  AlertTriangle,
  RefreshCw,
  Zap,
  Target,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Colores para niveles de demanda
const DEMAND_COLORS = {
  low: '#22C55E',      // Verde
  normal: '#3B82F6',   // Azul
  high: '#F59E0B',     // Amarillo/Naranja
  peak: '#EF4444',     // Rojo
};

const STATUS_COLORS = {
  online: '#22C55E',
  busy: '#F59E0B',
  idle: '#3B82F6',
  offline: '#6B7280',
};

// Componente para mostrar círculos de zona en el mapa
const ZoneCircles = ({ zones }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !zones || zones.length === 0) return;

    const circles = [];
    
    // Centros aproximados de zonas de GDL (hardcoded por ahora)
    const zoneCenters = {
      'GDL Centro': { lat: 20.6736, lng: -103.3442 },
      'Zapopan': { lat: 20.7167, lng: -103.4000 },
      'Tlaquepaque': { lat: 20.6167, lng: -103.3167 },
      'Tonalá': { lat: 20.6167, lng: -103.2333 },
      'Providencia': { lat: 20.6897, lng: -103.3906 },
      'CDMX Centro': { lat: 19.4326, lng: -99.1332 },
    };

    zones.forEach(zone => {
      const center = zoneCenters[zone.zone_name] || { lat: 20.6597, lng: -103.3496 };
      const color = DEMAND_COLORS[zone.demand_level] || DEMAND_COLORS.normal;
      
      const circle = new window.google.maps.Circle({
        strokeColor: color,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.25,
        map: map,
        center: center,
        radius: 2500 + (zone.orders_in_progress * 200), // Radio base + actividad
      });

      circles.push(circle);
    });

    return () => {
      circles.forEach(c => c.setMap(null));
    };
  }, [map, zones]);

  return null;
};

// Componente de marcador de rider
const RiderMarker = ({ rider, onClick }) => {
  if (!rider.current_location) return null;
  
  const position = {
    lat: rider.current_location.coordinates[1],
    lng: rider.current_location.coordinates[0],
  };

  const statusColor = STATUS_COLORS[rider.status] || STATUS_COLORS.offline;

  return (
    <AdvancedMarker position={position} onClick={() => onClick(rider)}>
      <div className="relative cursor-pointer group">
        {rider.status === 'busy' && (
          <div className="absolute -inset-2 bg-amber-400 rounded-full opacity-40 animate-ping" />
        )}
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-white transition-transform group-hover:scale-110"
          style={{ backgroundColor: statusColor }}
        >
          <Bike className="w-5 h-5 text-white" />
        </div>
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white px-2 py-0.5 rounded text-xs font-medium shadow whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          {rider.rider_name || 'Rider'}
        </div>
      </div>
    </AdvancedMarker>
  );
};

// Componente del mapa de Pulse
const PulseMap = ({ zones, riders, onRiderClick }) => {
  const map = useMap();

  useEffect(() => {
    if (map && zones.length > 0) {
      // Centrar en Guadalajara
      map.setCenter({ lat: 20.6597, lng: -103.3496 });
      map.setZoom(11);
    }
  }, [map, zones]);

  return (
    <Map 
      defaultZoom={11}
      defaultCenter={{ lat: 20.6597, lng: -103.3496 }}
      mapTypeId="roadmap"
      gestureHandling="greedy"
      style={{ width: '100%', height: '100%' }}
      styles={[
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ]}
    >
      <ZoneCircles zones={zones} />
      {riders.filter(r => r.current_location).map(rider => (
        <RiderMarker key={rider.rider_id} rider={rider} onClick={onRiderClick} />
      ))}
    </Map>
  );
};

const PulseDashboard = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [zones, setZones] = useState([]);
  const [riders, setRiders] = useState([]);
  const [hourlyDemand, setHourlyDemand] = useState([]);
  const [selectedRider, setSelectedRider] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchPulseData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    
    try {
      const [overviewRes, zonesRes, ridersRes, demandRes] = await Promise.all([
        axios.get('/pulse/overview'),
        axios.get('/pulse/zones'),
        axios.get('/pulse/riders'),
        axios.get('/pulse/demand/hourly?days=7'),
      ]);

      setOverview(overviewRes.data);
      setZones(zonesRes.data);
      setRiders(ridersRes.data);
      setHourlyDemand(demandRes.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching pulse data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPulseData();
    
    // Auto-refresh cada 30 segundos
    const interval = setInterval(() => fetchPulseData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchPulseData]);

  const handleRiderClick = (rider) => {
    setSelectedRider(rider);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} title="UID Pulse">
        <div className="flex items-center justify-center h-96">
          <CyberneticLoader size={100} message="Cargando UID Pulse..." />
        </div>
      </Layout>
    );
  }

  const demandLevelText = {
    low: 'Baja',
    normal: 'Normal',
    high: 'Alta',
    peak: 'Pico Máximo',
  };

  const kpiCards = [
    { 
      label: 'Eventos Hoy', 
      value: overview?.total_events_today || 0, 
      icon: Activity, 
      color: '#224049',
      bg: '#E8F0F1'
    },
    { 
      label: 'Riders Activos', 
      value: overview?.active_riders || 0, 
      icon: Users, 
      color: '#22C55E',
      bg: '#DCFCE7'
    },
    { 
      label: 'En Progreso', 
      value: overview?.orders_in_progress || 0, 
      icon: Package, 
      color: '#F59E0B',
      bg: '#FEF3C7'
    },
    { 
      label: 'Tiempo Prom.', 
      value: `${overview?.avg_delivery_time_minutes || 0} min`, 
      icon: Clock, 
      color: '#3B82F6',
      bg: '#DBEAFE'
    },
    { 
      label: 'SLA Express', 
      value: `${((overview?.sla_compliance_rate || 0) * 100).toFixed(1)}%`, 
      icon: Target, 
      color: overview?.sla_compliance_rate >= 0.95 ? '#22C55E' : '#EF4444',
      bg: overview?.sla_compliance_rate >= 0.95 ? '#DCFCE7' : '#FEE2E2'
    },
    { 
      label: 'Demanda', 
      value: demandLevelText[overview?.current_demand_level] || 'Normal', 
      icon: TrendingUp, 
      color: DEMAND_COLORS[overview?.current_demand_level] || DEMAND_COLORS.normal,
      bg: '#F3E8FF'
    },
  ];

  return (
    <Layout user={user} onLogout={onLogout} title="UID Pulse">
      <div className="space-y-6" data-testid="pulse-dashboard">
        {/* Header */}
        <div
          className="card-premium p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          style={{ background: 'linear-gradient(135deg, #224049 0%, #1a5a4a 50%, #0F7A5B 100%)' }}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
              <Activity className="w-8 h-8 text-white animate-pulse" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">UID Pulse</h1>
              <p className="text-sm mt-1 text-emerald-200">
                Inteligencia Geográfica en Tiempo Real
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {lastUpdate && (
              <span className="text-xs text-white/70">
                Actualizado: {lastUpdate.toLocaleTimeString('es-MX')}
              </span>
            )}
            <Button
              onClick={() => fetchPulseData(true)}
              disabled={refreshing}
              className="bg-white/10 hover:bg-white/20 text-white border-white/20"
              variant="outline"
              data-testid="pulse-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="pulse-kpi-grid">
          {kpiCards.map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <div
                key={i}
                className="metric-card hover:shadow-lg transition-shadow"
                data-testid={`pulse-kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{kpi.label}</span>
                  <div className="p-2 rounded-full" style={{ backgroundColor: kpi.bg }}>
                    <Icon className="w-4 h-4" style={{ color: kpi.color }} />
                  </div>
                </div>
                <div className="text-2xl font-bold" style={{ color: kpi.color }}>
                  {kpi.value}
                </div>
              </div>
            );
          })}
        </div>

        {/* Map + Zones Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Mapa principal */}
          <div className="lg:col-span-2 card-premium overflow-hidden" data-testid="pulse-map-container">
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" style={{ color: '#224049' }} />
                <h2 className="font-bold" style={{ color: '#224049' }}>Mapa de Operaciones</h2>
              </div>
              <div className="flex items-center gap-3 text-xs">
                {Object.entries(DEMAND_COLORS).map(([level, color]) => (
                  <div key={level} className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                    <span className="text-gray-600 capitalize">{demandLevelText[level]}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="h-[450px]">
              <MapProvider>
                <PulseMap 
                  zones={zones} 
                  riders={riders} 
                  onRiderClick={handleRiderClick}
                />
              </MapProvider>
            </div>
          </div>

          {/* Panel de Zonas */}
          <div className="card-premium" data-testid="pulse-zones-panel">
            <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: '#E5E7EB' }}>
              <Target className="w-5 h-5" style={{ color: '#224049' }} />
              <h2 className="font-bold" style={{ color: '#224049' }}>Zonas Activas</h2>
            </div>
            <div className="divide-y max-h-[400px] overflow-y-auto">
              {zones.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <MapPin className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Sin datos de zonas</p>
                </div>
              ) : (
                zones.map(zone => (
                  <div 
                    key={zone.zone_id} 
                    className="p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`zone-${zone.zone_id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold" style={{ color: '#224049' }}>
                        {zone.zone_name}
                      </span>
                      <span 
                        className="px-2 py-1 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: DEMAND_COLORS[zone.demand_level] }}
                      >
                        {demandLevelText[zone.demand_level]}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500 block">Pedidos</span>
                        <span className="font-semibold">{zone.orders_today}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">En Progreso</span>
                        <span className="font-semibold text-amber-600">{zone.orders_in_progress}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Riders</span>
                        <span className="font-semibold text-green-600">{zone.active_riders}</span>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <span className="text-gray-500">
                        SLA: <span className={zone.sla_compliance >= 0.95 ? 'text-green-600' : 'text-red-500'}>
                          {(zone.sla_compliance * 100).toFixed(0)}%
                        </span>
                      </span>
                      <span className="text-gray-500">
                        Prom: {zone.avg_delivery_minutes} min
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Riders + Demanda */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Panel de Riders */}
          <div className="card-premium" data-testid="pulse-riders-panel">
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
              <div className="flex items-center gap-2">
                <Bike className="w-5 h-5" style={{ color: '#224049' }} />
                <h2 className="font-bold" style={{ color: '#224049' }}>Riders en Tiempo Real</h2>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {Object.entries(STATUS_COLORS).slice(0, 3).map(([status, color]) => (
                  <div key={status} className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                    <span className="text-gray-600 capitalize">{status === 'busy' ? 'Ocupado' : status === 'idle' ? 'Libre' : 'Online'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="divide-y max-h-[350px] overflow-y-auto">
              {riders.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Sin riders activos</p>
                </div>
              ) : (
                riders.map(rider => (
                  <div 
                    key={rider.rider_id} 
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${selectedRider?.rider_id === rider.rider_id ? 'bg-blue-50' : ''}`}
                    onClick={() => setSelectedRider(rider)}
                    data-testid={`rider-${rider.rider_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                        style={{ backgroundColor: STATUS_COLORS[rider.status] }}
                      >
                        <Bike className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold" style={{ color: '#224049' }}>
                            {rider.rider_name || 'Rider'}
                          </span>
                          <span 
                            className="px-2 py-0.5 rounded text-xs font-medium"
                            style={{ 
                              backgroundColor: `${STATUS_COLORS[rider.status]}20`,
                              color: STATUS_COLORS[rider.status]
                            }}
                          >
                            {rider.status === 'busy' ? 'Ocupado' : rider.status === 'idle' ? 'Libre' : rider.status === 'online' ? 'Online' : 'Offline'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                          <span>Hoy: {rider.deliveries_today} entregas</span>
                          <span>Efic: {(rider.efficiency_today * 100).toFixed(0)}%</span>
                          {rider.avg_delivery_time > 0 && (
                            <span>Prom: {rider.avg_delivery_time} min</span>
                          )}
                        </div>
                      </div>
                      {rider.current_order_id && (
                        <div className="flex items-center text-amber-500">
                          <Zap className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Gráfico de Demanda por Hora */}
          <div className="card-premium p-6" data-testid="pulse-hourly-demand">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5" style={{ color: '#224049' }} />
              <h2 className="font-bold" style={{ color: '#224049' }}>Demanda por Hora (7 días)</h2>
            </div>
            {hourlyDemand.every(h => h.orders === 0) ? (
              <div className="flex items-center justify-center h-[300px] text-gray-500">
                <div className="text-center">
                  <AlertTriangle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Sin datos de demanda</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hourlyDemand}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="hour" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(h) => `${h}:00`}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    labelFormatter={(h) => `${h}:00 hrs`}
                    formatter={(value, name) => [value, name === 'orders' ? 'Pedidos' : 'Tiempo Prom']}
                  />
                  <Bar 
                    dataKey="orders" 
                    name="Pedidos" 
                    fill="#224049"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Modal de Rider Seleccionado */}
        {selectedRider && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedRider(null)}>
            <div 
              className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-4 mb-4">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white"
                  style={{ backgroundColor: STATUS_COLORS[selectedRider.status] }}
                >
                  <Bike className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-xl font-bold" style={{ color: '#224049' }}>
                    {selectedRider.rider_name || 'Rider'}
                  </h3>
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{ 
                      backgroundColor: `${STATUS_COLORS[selectedRider.status]}20`,
                      color: STATUS_COLORS[selectedRider.status]
                    }}
                  >
                    {selectedRider.status === 'busy' ? 'En Entrega' : selectedRider.status === 'idle' ? 'Disponible' : selectedRider.status === 'online' ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-xs text-gray-500 block">Entregas Hoy</span>
                  <span className="text-2xl font-bold" style={{ color: '#224049' }}>
                    {selectedRider.deliveries_today}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-xs text-gray-500 block">Eficiencia</span>
                  <span 
                    className="text-2xl font-bold"
                    style={{ color: selectedRider.efficiency_today >= 0.9 ? '#22C55E' : '#F59E0B' }}
                  >
                    {(selectedRider.efficiency_today * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-xs text-gray-500 block">Tiempo Promedio</span>
                  <span className="text-2xl font-bold" style={{ color: '#3B82F6' }}>
                    {selectedRider.avg_delivery_time || 0} min
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-xs text-gray-500 block">Pedido Actual</span>
                  <span className="text-sm font-medium" style={{ color: selectedRider.current_order_id ? '#F59E0B' : '#6B7280' }}>
                    {selectedRider.current_order_id ? `#${selectedRider.current_order_id.slice(0, 8)}` : 'Ninguno'}
                  </span>
                </div>
              </div>

              <Button 
                className="w-full"
                style={{ backgroundColor: '#224049' }}
                onClick={() => setSelectedRider(null)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PulseDashboard;
